import SaveAltIcon from '@mui/icons-material/SaveAlt';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import { subDays, format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthProvider, useNotify, Title } from 'react-admin';
import { useClinics } from '../../../hooks/useClinics';
import { GetAllClaimsParams, ClaimSortField, SortOrder } from '../../../interfaces/billing';
import { Identity } from '../../../interfaces/auth';
import { BillingUser, UnifiedClaim } from '../../../interfaces/billing.rpm';
import { SelectOption } from '../../../interfaces/common';
import { BillingService } from '../../../services';
import { formatDateStringToYYYYMMDD } from '../../../util/dateFormat';
import { exportToCSV } from '../../../util/csvExport';
import {
  Filters,
  INITIAL_PAGINATION,
  LoadingStates,
} from '../types/rpms.types';
import { RPMClaimsFilters } from './RPMClaimsFilters';
import { RPMClaimsTable } from './RPMClaimsTable';
import { RPM_CLAIMS_EXPORT_COLUMNS, generateClaimsExportFilename } from './claimsExportSchema';
import { isExportableStatus, isRPMClaimStatus } from '../utils/typeGuards';
import { getTotalSelectedCount } from '../utils/claimSelection';
import { CLAIMS, ERROR_MESSAGES, LOADING_SPINNER_SIZE } from '../constants';

// Initial filters with default date range and no status filter
const initialFilters: Filters = {
  clinic: [],
  insuranceCompany: [],
  dateOfService: [],
  invoiceCreationDateFrom: [format(subDays(new Date(), CLAIMS.DEFAULT_DATE_RANGE_DAYS), 'yyyy-MM-dd')],
  invoiceCreationDateTo: [format(new Date(), 'yyyy-MM-dd')],
  cptCodes: [],
  memberUuids: [],
  processIds: [],
  statuses: [], // Empty by default - no status filter
};

export const RPMClaims: React.FC = () => {
  const [data, setData] = useState<UnifiedClaim[]>([]);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [selectedClaims, setSelectedClaims] = useState<number[]>([]);
  const [allRecordsSelected, setAllRecordsSelected] = useState(false);
  const [excludedRows, setExcludedRows] = useState<{ id: number; index: number }[]>([]);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    isApplying: false,
    isClearing: false,
    isProcessing: false,
  });
  const [isCsvExporting, setIsCsvExporting] = useState(false);
  // Combined sort state to prevent race conditions
  const [sortState, setSortState] = useState<{
    column: ClaimSortField | null;
    direction: SortOrder;
  }>({ column: null, direction: 'desc' });

  const authProvider = useAuthProvider();
  const notify = useNotify();

  // Fetch clinics using custom hook
  const { clinics } = useClinics();

  const clinicOptions: SelectOption[] = clinics.map((clinic) => ({
    label: clinic.name,
    value: `${clinic.id}`
  }));

  const defaultClinicIds = useMemo(() => clinics.map((c) => c.id), [clinics]);

  const getClinicIds = useCallback(
    (selectedClinicIds: string[]) => {
      const parsed = selectedClinicIds.map((id) => parseInt(id)).filter((id) => !isNaN(id));
      return parsed.length > 0 ? parsed : defaultClinicIds;
    },
    [defaultClinicIds],
  );

  // Map table column keys to API sort fields
  const mapColumnToSortField = (columnKey: string): ClaimSortField | null => {
    const mapping: Record<string, ClaimSortField> = {
      'id': 'id',
      'clinic': 'clinicId',
      'insurance': 'insuranceCompanyId',
      'dateOfService': 'dateOfService',
      'invoiceCreationDate': 'createdAt',
      'processId': 'processId',
    };
    return mapping[columnKey] || null;
  };

  // Handler for column sort click
  const handleSort = useCallback((columnKey: string) => {
    const sortField = mapColumnToSortField(columnKey);
    if (!sortField) return;

    setSortState(prev => {
      if (prev.column === sortField) {
        // Same column clicked: toggle asc → desc → clear
        if (prev.direction === 'asc') {
          return { column: sortField, direction: 'desc' };
        } else {
          // Clear sort
          return { column: null, direction: 'desc' };
        }
      } else {
        // New column: start with ascending
        return { column: sortField, direction: 'asc' };
      }
    });
  }, []);

  // Transform filters to API parameters
  const transformFiltersToParams = useCallback((
    currentFilters: Filters,
    options?: { page?: number; limit?: number; sortBy?: ClaimSortField | null; sortOrder?: SortOrder }
  ): GetAllClaimsParams => {
    return {
      // Pagination
      page: options?.page,
      limit: options?.limit,
      // Date filters
      dateOfService: currentFilters.dateOfService?.[0]
        ? formatDateStringToYYYYMMDD(currentFilters.dateOfService[0])
        : undefined,
      createdAtFrom: currentFilters.invoiceCreationDateFrom?.[0]
        ? formatDateStringToYYYYMMDD(currentFilters.invoiceCreationDateFrom[0])
        : undefined,
      createdAtTo: currentFilters.invoiceCreationDateTo?.[0]
        ? formatDateStringToYYYYMMDD(currentFilters.invoiceCreationDateTo[0])
        : undefined,
      // Entity filters
      clinicIds: getClinicIds(currentFilters.clinic),
      insuranceCompanyIds: currentFilters.insuranceCompany.length > 0
        ? currentFilters.insuranceCompany
        : undefined,
      cptCodes: currentFilters.cptCodes.length > 0
        ? currentFilters.cptCodes
        : undefined,
      memberUuids: currentFilters.memberUuids.length > 0
        ? currentFilters.memberUuids
        : undefined,
      processIds: currentFilters.processIds.length > 0
        ? currentFilters.processIds.map((id) => parseInt(id))
        : undefined,
      statuses: currentFilters.statuses.length > 0
        ? currentFilters.statuses
        : undefined,
      // Sorting
      sortBy: options?.sortBy || undefined,
      sortOrder: options?.sortBy ? options?.sortOrder : undefined,
    };
  }, [getClinicIds]);

  // Simplified fetchClaims using unified GET /claims endpoint
  const fetchClaims = useCallback(async (currentFilters: Filters, page: number, limit: number, signal?: AbortSignal) => {
    setLoadingStates((prev) => ({ ...prev, isApplying: true }));

    try {
      // Check if request was cancelled
      if (signal?.aborted) {
        return;
      }

      const billingService = new BillingService();

      // Build parameters for unified endpoint
      const params = transformFiltersToParams(currentFilters, {
        page,
        limit,
        sortBy: sortState.column,
        sortOrder: sortState.direction,
      });

      const response = await billingService.getAllClaims(params);

      // Validate response structure
      if (!response || typeof response !== 'object') {
        console.error('Invalid response from getAllClaims: response is not an object', response);
        notify(ERROR_MESSAGES.INVALID_RESPONSE, { type: 'error' });
        return;
      }

      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid response from getAllClaims: data is not an array', response);
        notify(ERROR_MESSAGES.INVALID_RESPONSE, { type: 'error' });
        return;
      }

      // Set data
      setData(response.data);

      // Log any unknown statuses for monitoring
      const unknownStatuses = response.data
        .filter(claim => !isRPMClaimStatus(claim.status))
        .map(claim => claim.status);

      if (unknownStatuses.length > 0) {
        console.warn('Found claims with unknown statuses:', Array.from(new Set(unknownStatuses)));
      }

      // Handle pagination with fallback
      if (!response.pagination) {
        console.warn('Missing pagination in response, using defaults');
        setPagination({
          totalCount: response.data.length,
          totalPages: 1,
          currentPage: page,
          limit,
        });
      } else {
        setPagination(response.pagination);
      }

      // Reset selection when data changes
      setSelectedClaims([]);
      setAllRecordsSelected(false);
      setExcludedRows([]);
    } catch (error) {
      // Ignore abort errors - they're expected when user changes filters quickly
      if (signal?.aborted || (error as Error).name === 'AbortError') {
        return;
      }
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        notify(ERROR_MESSAGES.PERMISSION_DENIED, { type: 'error' });
        return;
      }
      console.error('Error fetching claims:', error);
      notify(ERROR_MESSAGES.CLAIMS_FETCH_FAILED, { type: 'error' });
    } finally {
      // Don't update loading state if request was aborted
      if (!signal?.aborted) {
        setLoadingStates((prev) => ({ ...prev, isApplying: false }));
      }
    }
  }, [notify, sortState, transformFiltersToParams]);

  // Fetch data when filters, pagination, sorting, or clinics change
  useEffect(() => {
    if (clinics.length === 0) return;

    const abortController = new AbortController();

    fetchClaims(filters, pagination.currentPage, pagination.limit, abortController.signal);

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.currentPage, pagination.limit, sortState, clinics]);

  const handleApplyFilters = (appliedFilters: Filters) => {
    setFilters(appliedFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // fetchClaims will be called automatically by useEffect
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSelectedClaims([]);
    // fetchClaims will be called automatically by useEffect
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      limit: newLimit,
      currentPage: 1,
    }));
  };

  const handleExportSelectedClaims = async () => {
    setLoadingStates((prev) => ({ ...prev, isProcessing: true }));

    try {
      const identity = (await authProvider.getIdentity!()) as Identity;
      if (!identity?.uuid || !identity?.fullName) {
        notify(ERROR_MESSAGES.INVALID_STORAGE, { type: 'error' });
        setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
        return;
      }

      const user: BillingUser = {
        uuid: identity.uuid,
        fullName: identity.fullName,
      };

      // Determine claim IDs to export based on selection mode
      let claimIds: { select: number[]; exclude: number[] };

      if (allRecordsSelected) {
        // When "Select All" is used, let backend find all matching claims
        // We only pass the excluded claim IDs
        claimIds = {
          select: [],
          exclude: excludedRows
            .map(row => row.id)
            .filter(id => {
              const claim = data.find(c => c.id === id);
              return claim && isExportableStatus(claim.status);
            }),
        };
      } else {
        // When specific claims are selected, pass only those IDs
        const exportableClaimIds = selectedClaims.filter(claimId => {
          const claim = data.find(c => c.id === claimId);
          return claim && isExportableStatus(claim.status);
        });

        // Validate that we have exportable claims
        if (exportableClaimIds.length === 0) {
          notify(ERROR_MESSAGES.NO_EXPORTABLE_CLAIMS, { type: 'warning' });
          setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
          return;
        }

        // Show info if some claims were filtered out
        if (exportableClaimIds.length < selectedClaims.length) {
          notify(
            `Downloading ${exportableClaimIds.length} of ${selectedClaims.length} selected claims. Created claims are not downloadable.`,
            { type: 'info' }
          );
        }

        claimIds = {
          select: exportableClaimIds,
          exclude: [],
        };
      }

      // Build complete filters for note export (without pagination)
      // When allRecordsSelected=true, backend will use these filters to find all matching claims
      const filtersForExport = {
        clinicIds: getClinicIds(filters.clinic),
        ...(filters.insuranceCompany.length > 0 && {
          insuranceCompanyIds: filters.insuranceCompany
        }),
        ...(filters.cptCodes.length > 0 && {
          cptCodes: filters.cptCodes
        }),
        ...(filters.dateOfService?.[0] && {
          dateOfService: formatDateStringToYYYYMMDD(filters.dateOfService[0])
        }),
        ...(filters.memberUuids.length > 0 && {
          memberUuids: filters.memberUuids
        }),
        ...(filters.processIds.length > 0 && {
          processId: parseInt(filters.processIds[0])
        }),
        ...(filters.invoiceCreationDateFrom?.[0] && {
          invoiceCreationDateFrom: formatDateStringToYYYYMMDD(filters.invoiceCreationDateFrom[0])
        }),
        ...(filters.invoiceCreationDateTo?.[0] && {
          invoiceCreationDateTo: formatDateStringToYYYYMMDD(filters.invoiceCreationDateTo[0])
        }),
        ...(filters.statuses.length > 0 && {
          statuses: filters.statuses
        }),
      };

      const billingService = new BillingService();
      const response = await billingService.exportNotes(
        claimIds,
        filtersForExport,
        user,
        { runInBackground: true }
      );

      if (response) {
        // Show different notifications based on response type
        if (billingService.isBackgroundResponse(response)) {
          notify('Note export started in the background. You will see updates in real-time.', { type: 'info' });
        } else {
          const summary = billingService.createProcessSummary(response);
          notify(`Note export completed. ${summary}`, { type: 'success' });
        }

        setSelectedClaims([]);
        setAllRecordsSelected(false);
        setExcludedRows([]);
      }
    } catch (error) {
      console.error('Error exporting claims:', error);
      notify('Error creating export job', { type: 'error' });
    } finally {
      setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
    }
  };

  const handleExportToCSV = async () => {
    setIsCsvExporting(true);
    try {
      // Yield to the event loop to ensure loading state renders before synchronous CSV generation
      // This prevents UI blocking when processing large datasets (1000+ claims)
      await new Promise(resolve => setTimeout(resolve, 0));

      let claimsToExport: UnifiedClaim[];

      if (allRecordsSelected) {
        // Fetch all claims matching current filters (across all pages)
        const billingService = new BillingService();
        const params = transformFiltersToParams(filters, {
          page: 1,
          limit: 10000, // High limit to get all claims
          sortBy: sortState.column,
          sortOrder: sortState.direction,
        });

        const response = await billingService.getAllClaims(params);

        if (!response || !response.data) {
          notify(ERROR_MESSAGES.CLAIMS_FETCH_FAILED, { type: 'error' });
          return;
        }

        // Apply exclusions
        claimsToExport = response.data.filter(
          claim => !excludedRows.some(row => row.id === claim.id)
        );
      } else {
        // Export only selected claims from current page data
        claimsToExport = data.filter(claim => selectedClaims.includes(claim.id));
      }

      if (claimsToExport.length === 0) {
        notify(ERROR_MESSAGES.NO_CLAIMS_SELECTED, { type: 'warning' });
        return;
      }

      // Validate data before export
      const invalidClaims = claimsToExport.filter(claim =>
        !claim || !claim.id || !claim.billingMember
      );

      if (invalidClaims.length > 0) {
        console.error('Found invalid claims during export:', invalidClaims);
        notify(`Warning: ${invalidClaims.length} claim(s) have incomplete data and may not export correctly`, { type: 'warning' });
      }

      // Generate filename with timestamp
      const filename = generateClaimsExportFilename('rpm-claims-export');

      // Export to CSV
      exportToCSV(claimsToExport, RPM_CLAIMS_EXPORT_COLUMNS, { filename });

      // Show success message
      notify(`Successfully exported ${claimsToExport.length} claim${claimsToExport.length !== 1 ? 's' : ''} to CSV`, { type: 'success' });
    } catch (error) {
      console.error('Error exporting claims to CSV:', error);
      notify(ERROR_MESSAGES.CSV_EXPORT_FAILED, { type: 'error' });
    } finally {
      setIsCsvExporting(false);
    }
  };

  const hasSelectedClaims = selectedClaims.length > 0 || allRecordsSelected;

  // Calculate exportable claims count for button display
  const exportableClaimsCount = useMemo(() => {
    if (allRecordsSelected) {
      return data
        .filter(claim => isExportableStatus(claim.status))
        .filter(claim => !excludedRows.some(row => row.id === claim.id))
        .length;
    } else {
      return selectedClaims.filter(claimId => {
        const claim = data.find(c => c.id === claimId);
        return claim && isExportableStatus(claim.status);
      }).length;
    }
  }, [data, selectedClaims, allRecordsSelected, excludedRows]);

  const totalSelectedCount = getTotalSelectedCount(
    pagination.totalCount,
    selectedClaims,
    allRecordsSelected,
    excludedRows
  );

  return (
    <>
      <Title title="RPM Claims Search" />
      <Box sx={{ padding: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          RPM Claims Search
        </Typography>

        {clinics.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <RPMClaimsFilters
              clinicOptions={clinicOptions}
              filters={filters}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
              loadingStates={loadingStates}
              selectedCount={totalSelectedCount}
              additionalButtons={
                <>
                  <Tooltip title="Download notes for exported claims only. Created claims will be excluded.">
                    <span>
                      <Button
                        variant="contained"
                        startIcon={loadingStates.isProcessing ? <CircularProgress size={LOADING_SPINNER_SIZE} /> : <SaveAltIcon />}
                        onClick={handleExportSelectedClaims}
                        disabled={!hasSelectedClaims || loadingStates.isApplying || loadingStates.isProcessing}
                      >
                        {loadingStates.isProcessing ? 'Creating Export...' : 'Download Selected Notes'}
                        {exportableClaimsCount > 0 && exportableClaimsCount < totalSelectedCount &&
                          ` (${exportableClaimsCount}/${totalSelectedCount})`
                        }
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Export selected claims to CSV format">
                    <span>
                      <Button
                        variant="contained"
                        startIcon={isCsvExporting ? <CircularProgress size={20} /> : <TableChartOutlinedIcon />}
                        onClick={handleExportToCSV}
                        disabled={!hasSelectedClaims || loadingStates.isApplying || isCsvExporting}
                      >
                        {isCsvExporting ? 'Exporting...' : 'Export to CSV'}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              }
            />

            <RPMClaimsTable
              data={data}
              pagination={pagination}
              selectedClaims={selectedClaims}
              allRecordsSelected={allRecordsSelected}
              excludedRows={excludedRows}
              onSelectionChange={setSelectedClaims}
              onSelectAll={setAllRecordsSelected}
              onExcludeRows={setExcludedRows}
              onPageChange={handlePageChange}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              sortColumn={sortState.column}
              sortDirection={sortState.direction}
              onSort={handleSort}
            />
          </>
        )}
      </Box>
    </>
  );
};